import { useEffect, useRef, useState } from "react";
import { MousePointer2, Globe, MailCheck, RotateCcw, CheckCircle2, Zap } from "lucide-react";

const ACCENT = "#BEF264";

const STEPS = [
  { key: "submit", label: "A visitor completes an enquiry form" },
  { key: "capture", label: "A CRM record is created or updated" },
  { key: "followup", label: "An automated acknowledgement is sent" },
  { key: "pipeline", label: "The lead enters the pipeline with a next action" },
];
const HOLD = STEPS.length; // final resting frame before looping

export default function SalesDemo() {
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const ref = useRef(null);
  const reducedRef = useRef(false);

  useEffect(() => {
    reducedRef.current = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => setPlaying(e.isIntersecting)),
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!playing) return;
    if (reducedRef.current) { setStep(HOLD); return; }
    const t = setInterval(() => setStep((s) => (s >= HOLD ? 0 : s + 1)), 1700);
    return () => clearInterval(t);
  }, [playing]);

  const replay = () => { setStep(0); setPlaying(true); };

  const submitting = step === 0;
  const leadInCrm = step >= 1;
  const followupSent = step >= 2;
  const pipelineStage = step >= 3 ? 1 : 0; // 0=New, 1=Contacted

  return (
    <section ref={ref} id="demo" aria-labelledby="demo-heading" data-testid="sales-demo-section" className="relative py-20 sm:py-28 bg-sales-navy text-white overflow-hidden">
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-1/4 left-1/3 h-72 w-72 rounded-full blur-3xl" style={{ background: `${ACCENT}14` }} />
      </div>

      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="text-center max-w-3xl mx-auto mb-4">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.05] px-3.5 py-1.5 text-[11px] tracking-[0.14em] uppercase text-white/70 font-semibold mb-6">
            <Zap className="h-3.5 w-3.5" style={{ color: ACCENT }} /> Lead capture &amp; sales automation demo
          </div>
          <h2 id="demo-heading" className="font-display text-4xl sm:text-5xl font-black tracking-[-0.03em] leading-[1.04]">
            See How a Website Enquiry Becomes a <span style={{ color: ACCENT }}>Sales Opportunity</span>
          </h2>
          <p data-testid="sales-demo-label" className="mt-4 text-xs uppercase tracking-[0.16em] text-white/45">
            Illustrative demonstration · names and records are fictional
          </p>
        </div>

        {/* step captions */}
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-2 max-w-4xl mx-auto">
          {STEPS.map((s, i) => {
            const done = step > i || step === HOLD;
            const active = step === i;
            return (
              <div
                key={s.key}
                className={`rounded-xl border px-3 py-2.5 transition-all duration-300 ${
                  active ? "border-white/25 bg-white/[0.08]" : done ? "border-white/12 bg-white/[0.03]" : "border-white/8 bg-transparent"
                }`}
              >
                <div className="flex items-center gap-1.5">
                  <span
                    className="h-4 w-4 rounded-full flex items-center justify-center text-[9px] font-black transition-colors"
                    style={done || active ? { background: ACCENT, color: "#0B1120" } : { background: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.5)" }}
                  >
                    {done ? "✓" : i + 1}
                  </span>
                  <span className={`text-[11px] font-semibold leading-tight ${active || done ? "text-white" : "text-white/45"}`}>{s.label}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* stage */}
        <div className="mt-8 grid lg:grid-cols-[1fr_auto_1fr] gap-4 lg:gap-2 items-center">
          {/* LEFT — landing page + form */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.8)]">
            <div className="flex items-center gap-1.5 mb-3">
              <span className="h-2 w-2 rounded-full bg-white/20" />
              <span className="h-2 w-2 rounded-full bg-white/20" />
              <span className="h-2 w-2 rounded-full bg-white/20" />
              <div className="ml-2 flex items-center gap-1.5 rounded bg-white/[0.06] px-2 py-0.5 text-[9px] font-mono text-white/50">
                <Globe className="h-2.5 w-2.5" /> yourbusiness.co.za
              </div>
            </div>
            <div className="rounded-xl bg-white p-4">
              <div className="text-[13px] font-black text-zinc-900">Get a free consultation</div>
              <div className="mt-3 rounded-md bg-zinc-100 border border-zinc-200 px-2.5 py-1.5 text-[11px] text-zinc-700">
                Naledi Khumalo{submitting && <span className="inline-block w-px h-3 align-middle ml-0.5 bg-zinc-800 animate-pulse" />}
              </div>
              <div className="mt-2 rounded-md bg-zinc-100 border border-zinc-200 px-2.5 py-1.5 text-[11px] text-zinc-700">
                naledi@brightco.co.za
              </div>
              <div className="mt-3 relative inline-block">
                <button
                  className={`relative inline-flex items-center rounded-md px-4 py-1.5 text-[11px] font-bold text-zinc-900 transition-transform ${submitting ? "scale-95" : ""}`}
                  style={{ background: ACCENT }}
                >
                  Submit enquiry
                </button>
                {submitting && <span className="absolute inset-0 rounded-md ring-2 ring-[#BEF264] animate-ping" />}
                <MousePointer2 className={`absolute -right-4 -bottom-3 h-4 w-4 text-zinc-800 transition-all ${submitting ? "translate-x-0 translate-y-0 opacity-100" : "translate-x-2 translate-y-2 opacity-60"}`} />
              </div>
            </div>
          </div>

          {/* connector */}
          <div className="relative h-8 lg:h-40 flex lg:flex-col items-center justify-center">
            <div className="relative w-full lg:w-px h-px lg:h-full bg-white/10">
              <span
                className="absolute rounded-full transition-all duration-700 ease-out"
                style={{
                  background: ACCENT,
                  height: 8, width: 8,
                  boxShadow: `0 0 12px ${ACCENT}`,
                  left: "50%",
                  top: "50%",
                  transform: leadInCrm ? "translate(-50%,-50%) scale(1)" : "translate(-50%,-50%) scale(0)",
                  opacity: step === 1 ? 1 : 0,
                }}
              />
            </div>
          </div>

          {/* RIGHT — sales system dashboard */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.8)]">
            <div className="text-[10px] font-bold uppercase tracking-wider text-white/50 mb-3">Your Sales System</div>

            {/* CRM contact */}
            <div
              className={`rounded-xl border p-3 transition-all duration-500 ${leadInCrm ? "opacity-100 translate-y-0 border-[#BEF264]/40 bg-[#BEF264]/[0.06]" : "opacity-0 translate-y-3 border-white/10"}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="h-8 w-8 rounded-full flex items-center justify-center text-[10px] font-black text-zinc-900" style={{ background: ACCENT }}>NK</div>
                  <div>
                    <div className="text-[12px] font-bold text-white leading-tight">Naledi Khumalo</div>
                    <div className="text-[9px] text-white/50">Source: Google Ads</div>
                  </div>
                </div>
                {leadInCrm && step === 1 && <span className="text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ background: `${ACCENT}22`, color: ACCENT }}>+ New</span>}
              </div>
            </div>

            {/* auto follow-up */}
            <div className={`mt-2.5 rounded-xl border border-white/10 bg-white/[0.05] p-3 transition-all duration-500 ${followupSent ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
              <div className="flex items-center gap-1.5 text-[10px] font-bold text-white/70">
                <MailCheck className="h-3.5 w-3.5" style={{ color: ACCENT }} /> Auto follow-up
                {followupSent && <span className="ml-auto text-[8px] font-semibold uppercase tracking-wider" style={{ color: ACCENT }}>Sent</span>}
              </div>
              <div className="mt-1.5 text-[10px] text-white/55 leading-snug">"Hi Naledi, thanks for your enquiry — here's what happens next…"</div>
            </div>

            {/* pipeline */}
            <div className="mt-2.5 rounded-xl border border-white/10 bg-white/[0.05] p-3">
              <div className="text-[10px] font-bold text-white/70 mb-2">Pipeline</div>
              <div className="flex items-center gap-2">
                {["New lead", "Contacted", "Qualified"].map((s, i) => {
                  const on = i === pipelineStage && leadInCrm;
                  const passed = i < pipelineStage && leadInCrm;
                  return (
                    <div key={s} className="flex items-center gap-2">
                      <div
                        className={`flex items-center gap-1 rounded-full px-2 py-1 text-[9px] font-bold transition-all duration-400 ${
                          on ? "text-[#0B1120]" : passed ? "text-white/40" : "text-white/40 bg-white/[0.05]"
                        }`}
                        style={on ? { background: ACCENT } : undefined}
                      >
                        {on && <CheckCircle2 className="h-2.5 w-2.5" />} {s}
                      </div>
                      {i < 2 && <span className="text-white/20 text-[9px]">→</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex items-center justify-center gap-4">
          <button onClick={replay} data-testid="sales-demo-replay" className="inline-flex items-center gap-1.5 rounded-full h-11 px-5 text-sm font-semibold text-white border border-white/15 bg-white/[0.04] hover:bg-white/[0.09] transition-colors">
            <RotateCcw className="h-4 w-4" style={{ color: ACCENT }} /> Replay
          </button>
          <a href="#request-plan" data-testid="sales-demo-cta" className="inline-flex items-center rounded-full h-11 px-6 text-sm font-bold text-[#0B1120] transition-all hover:brightness-105" style={{ background: ACCENT }}>
            Build This for My Business
          </a>
        </div>
      </div>
    </section>
  );
}
